// M29 — Invite-code helper. Wraps the `redeem_alpha_invite_code` RPC so
// the Register page can call a single function and get a friendly error
// message back. The RPC is anon-callable (signup happens before
// authentication exists), and idempotent on (code, email) so a retry
// after a transient signup failure doesn't waste a slot.

import { supabase } from './supabase';

export class InviteCodeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InviteCodeError';
  }
}

export async function redeemInviteCode(code: string, email: string): Promise<void> {
  const trimmedCode = code.trim();
  const trimmedEmail = email.trim().toLowerCase();
  if (!trimmedCode) throw new InviteCodeError('An invite code is required.');
  if (!trimmedEmail) throw new InviteCodeError('An email is required.');

  const { error } = await supabase.rpc('redeem_alpha_invite_code', {
    p_code: trimmedCode,
    p_email: trimmedEmail,
  });
  if (error) {
    throw new InviteCodeError(friendlyInviteError(error.message));
  }
}

function friendlyInviteError(message: string | undefined | null): string {
  if (!message) return 'We could not validate that invite code. Please try again.';
  const m = message.toLowerCase();
  if (m.includes('invalid invite code')) {
    return "That invite code isn't valid. Double-check the spelling and try again.";
  }
  if (m.includes('no longer active')) {
    return 'This invite code has been deactivated. Reach out for a new one.';
  }
  if (m.includes('expired')) {
    return 'This invite code has expired. Reach out for a new one.';
  }
  if (m.includes('fully used')) {
    return 'This invite code has been fully used. Reach out for a new one.';
  }
  if (m.includes('required')) {
    return 'An invite code is required.';
  }
  return 'We could not validate that invite code. Please try again.';
}
